'use client'
import * as React from 'react'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import { useTabsStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import type { EditorEvents, NodeViewProps } from '@tiptap/react'
import { NestedDocumentBlockLink } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/document/nested/link'
import { clsx } from 'clsx'
import {
  isComponentBlock,
  isInstanceBlock,
  isRichTextBlock,
} from '@basehub/data/replicache/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getIssueProps } from '../linter'
import { useRichTextStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/rich-text/store'
import { getInvalidReferencedBlock } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/helpers'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const BaseHubBlockNodeView: React.FC<NodeViewProps> = ({
  node,
  selected,
  editor,
  getPos,
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const addTab = useTabsStore((s) => s.addTab)
  const workingTree = useWorkingTreeStore((s) => s.tree)
  const [wasUpdated, setWasUpdated] = React.useState(false)
  const [isDragging, setIsDragging] = React.useState(false)
  const [isBetween, setIsBetween] = React.useState(false)

  const prevSelected = React.useRef(selected)
  // get block from working tree and path
  const { block, path, parent } =
    useTreeSlice(
      workingTree,
      (wt) => {
        const _block = wt?.getNode(node.attrs.id)
        const parent = _block?.parentKey ? wt?.getNode(_block.parentKey) : null
        const path = wt?.getPath(node.attrs.id, { format: 'string' })
        return {
          block: _block,
          path,
          parent,
        }
      },
      'shallow',
      [node.attrs.id]
    ) ?? {}

  const recentlyCreatedBlock = useRichTextStore(
    (s) => s.recentlyCreatedBlock,
    true
  )
  const setRecentlyCreatedBlock = useRichTextStore(
    (s) => s.setRecentlyCreatedBlock,
    true
  )
  const isRecentlyCreated = useRichTextStore((s) => {
    return (
      s.recentlyCreatedBlock?.id === node.attrs.id ||
      s.recentlyCreatedBlock?.sourceBlockId === node.attrs.id
    )
  }, true)

  // get the block to display in the most efficient way
  const blockToDisplay = React.useMemo(() => {
    if (!block) return getInvalidReferencedBlock(node.attrs.id)
    return block &&
      (isComponentBlock(block.value) || isInstanceBlock(block.value))
      ? block.value
      : recentlyCreatedBlock
  }, [block, node.attrs.id, recentlyCreatedBlock])

  const openNewTab = React.useCallback(
    (e: KeyboardEvent) => {
      if (!editor.isFocused) return
      if (prevSelected.current && block && e.key === 'Enter') {
        addTab({ key: block?.key, partiallyOpen: true })
        editor.commands.undo()
      } else prevSelected.current = selected
      if (wasUpdated) setWasUpdated(false)
    },
    [block, selected, addTab, editor, wasUpdated]
  )

  const handleOtherKeys = React.useCallback(() => {
    prevSelected.current = selected
    if (wasUpdated) setWasUpdated(false)
  }, [selected, wasUpdated])

  React.useEffect(() => {
    window.addEventListener('keydown', openNewTab)
    window.addEventListener('click', handleOtherKeys)
    return () => {
      window.removeEventListener('keydown', openNewTab)
      window.removeEventListener('click', handleOtherKeys)
    }
  }, [block, openNewTab, handleOtherKeys])

  const handleSelectionUpdate = React.useCallback(
    ({ editor }: EditorEvents['selectionUpdate']) => {
      if (
        editor.state.selection.ranges[0] &&
        editor.state.selection.ranges[0].$from.depth > 0 &&
        editor.state.selection.to - editor.state.selection.from > 0 &&
        editor.state.selection.to >= getPos() &&
        editor.state.selection.from <= getPos() + node.nodeSize
      ) {
        setIsBetween(true)
        return
      }
      setIsBetween(false)
    },
    [getPos, node.nodeSize]
  )

  React.useEffect(() => {
    editor.on('selectionUpdate', handleSelectionUpdate)

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [editor, handleSelectionUpdate])

  const [title, setTitle] = React.useState(blockToDisplay?.title ?? '')

  React.useEffect(() => {
    setTitle(blockToDisplay?.title ?? '')
  }, [blockToDisplay?.title])

  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isRecentlyCreated) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 10)
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecentlyCreated, inputRef.current])

  const updateBlock = React.useCallback(async () => {
    if (!replicache) return
    if (!block) return
    if (!path) return
    if (block.value.title === title) return
    await replicache.mutate.updateBlock({
      id: block.value.id,
      path,
      data: {
        title,
      },
      instanceChildTarget: block.value.instanceChildTarget,
      isoNow: new Date().toISOString(),
      strategy: 'merge-deep',
    })
    setWasUpdated(true)
  }, [path, replicache, title, block])

  const [isEditorFocused, setIsEditorFocused] = React.useState(editor.isFocused)

  React.useEffect(() => {
    function handleBlur() {
      setIsEditorFocused(false)
    }

    function handleFocus() {
      setIsEditorFocused(true)
    }

    editor.on('blur', handleBlur)
    editor.on('focus', handleFocus)

    return () => {
      editor.off('blur', handleBlur)
      editor.off('focus', handleFocus)
    }
  }, [editor])

  const shouldShowSelected =
    (!isDragging && (isBetween || selected) && isEditorFocused) || wasUpdated

  let issueProps: any = {}
  if (parent?.value && isRichTextBlock(parent.value)) {
    const allowedComponents = parent.value.constraints.allowedComponents

    if (block) {
      const targetId = isInstanceBlock(block.value)
        ? block.value.typeOptions.targetBlockId
        : block.value.id
      if (!allowedComponents?.includes(targetId)) {
        issueProps = getIssueProps({
          type: 'node',
          message: 'Component type not allowed',
          from: 0,
          to: 0,
          level: 'error',
        })
        issueProps.className = issueProps.class
        delete issueProps.class
      }
    } else {
      issueProps = getIssueProps({
        type: 'node',
        message: 'Unresolved block',
        from: 0,
        to: 0,
        level: 'error',
      })
      issueProps.className = issueProps.class
      delete issueProps.class
    }
  }

  return (
    <NodeViewWrapper
      contentEditable={false}
      className="mb-4 relative cursor-default not-prose"
      id={node.attrs.id}
      data-id={node.attrs.id}
      data-node-view-wrapper
      data-node-selected={shouldShowSelected}
      {...issueProps}
    >
      <div
        data-id={node.attrs.id}
        id={node.attrs.id}
        className="group/node-view-wrapper"
        contentEditable={false}
      >
        {blockToDisplay && (
          <div className="relative">
            <NestedDocumentBlockLink
              allowOptions={false}
              block={{ ...blockToDisplay, title }}
              path={path ?? ''}
              parentBlock={parent?.value ?? null}
              className={clsx('pointer-events-none')}
              wrapperClassName={clsx(
                'focus-visible:outline outline-dark-control-base !mx-0',
                shouldShowSelected && 'outline'
              )}
              data-drag-handle
              draggable={true}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={() => setIsDragging(false)}
              mode="withActions"
              hidePreview={isDragging}
            >
              {isRecentlyCreated && (
                <input
                  ref={inputRef}
                  className="relative z-30 w-[75%] border-transparent ring-transparent px-1 py-0.5 rounded-sm text-sm font-medium bg-transparent border-none ring-0 outline-none focus:ring-0 focus:outline-none focus:border-transparent focus:ring-transparent focus-visible:ring-0 focus-visible:outline-none focus-visible:border-transparent focus-visible:ring-transparent"
                  autoFocus
                  value={title}
                  onBlur={async () => {
                    if (!title) return
                    await updateBlock()
                    setRecentlyCreatedBlock?.(null)
                    inputRef.current?.blur()
                  }}
                  onKeyDown={async (e) => {
                    e.stopPropagation()
                    if (
                      ['Enter', 'Escape', 'ArrowDown', 'ArrowUp'].includes(
                        e.key
                      )
                    ) {
                      e.preventDefault()
                      await updateBlock()
                      switch (e.key) {
                        case 'Enter': {
                          editor
                            .chain()
                            .focus()
                            .setNodeSelection(getPos())
                            .run()
                          setWasUpdated(true)
                          prevSelected.current = true
                          break
                        }
                        case 'ArrowDown': {
                          // if there is a next block, select it
                          // otherwise create an empty paragraph
                          const nextBlock = editor.state.doc.nodeAt(
                            getPos() + node.nodeSize
                          )
                          if (nextBlock) {
                            nextBlock?.textContent
                              ? editor
                                  .chain()
                                  .focus()
                                  .setTextSelection(getPos() + node.nodeSize)
                                  .run()
                              : editor
                                  .chain()
                                  .focus()
                                  .setNodeSelection(getPos() + node.nodeSize)
                                  .run()
                          } else {
                            editor
                              .chain()
                              .focus()
                              .insertContentAt(getPos() + node.nodeSize, {
                                type: 'paragraph',
                                content: [],
                              })
                              .focus()
                              .run()
                          }
                          prevSelected.current = false
                          break
                        }
                        case 'ArrowUp': {
                          // if there is a next block, select it
                          // otherwise create an empty paragraph
                          const prevBlock =
                            getPos() > 0
                              ? editor.state.doc.nodeAt(getPos() - 1)
                              : null
                          if (prevBlock) {
                            prevBlock?.textContent
                              ? editor
                                  .chain()
                                  .focus()
                                  .setTextSelection(getPos() - 1)
                                  .run()
                              : editor
                                  .chain()
                                  .focus()
                                  .setNodeSelection(getPos() - 1)
                                  .run()
                          } else {
                            editor
                              .chain()
                              .focus()
                              .insertContentAt(getPos(), {
                                type: 'paragraph',
                                content: [],
                              })
                              .focus()
                              .run()
                          }
                          prevSelected.current = false
                          break
                        }
                        case 'Escape': {
                          editor
                            .chain()
                            .focus()
                            .setNodeSelection(getPos())
                            .run()
                          prevSelected.current = true
                          break
                        }
                      }
                      setRecentlyCreatedBlock?.(null)
                      inputRef.current?.blur()
                    }
                  }}
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </NestedDocumentBlockLink>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  )
}

export const RenderedBaseHubBlockNodeView =
  ReactNodeViewRenderer(BaseHubBlockNodeView)
